import React, { useEffect, useRef, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import API from '@api/index';
import { BRAND_DTO_ENUMS } from 'api/repositories/brand/brand.constants';
import { useLanguage } from '@languages/index';

import OverlayBrandTaxInvoiceInfoForm, { OverlayBrandTaxInvoiceInfoFormResult } from 'components/overlayBrandTaxInvoiceInfoForm';
import { isKoreaService } from 'constants/domains/domains';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';
import useAppState from 'hooks/useAppState';
import PaymentCreatePackage from 'pages/payments/createPackage';
import PaymentComplete from 'pages/payments/complete';

const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY);

const ButtonPostingVideoDownload = (props: {
  renderButton: (onClick: (e: any) => void) => JSX.Element;
  // REQUEST
  brandToken?: string;
  // REQUEST-VIDEO
  postingVideoId: number;
  fileName?: string;
  onChangePostingVideo: (postingVideo: any) => void;
  // REQUEST-PAYMENT
  isPaymentRequired: boolean;
  paymentProductType?: string; // PAYMENT_PRODUCT_TYPE
  paymentPrice: number;
  postingId: number;
  campaignId?: number;
  productId?: number;
  // FROM REDUX STORE
  showModal: any;
  showOverlays: any;
  closeOverlays: any;
}) => {
  const { state, actions } = useAppState(['brand'])

  const language = useLanguage();
  const languageTexts = {
    ...language.common,
    ...language.component.buttonCleanVideoDownload,
  };

  async function fetchPostingVideo() {
    const response = await API.PostVideos.show({
      id: props.postingVideoId,
      brandToken: props.brandToken,
    });
    return response?.data?.postingVideo;
  }

  async function processPayment() {
    let isConfirmed = await showPaymentConfirmationModal();
    if (!isConfirmed) {
      return;
    }

    let isPaid = await showPaymentModal();
    return isPaid;
  }

  async function showPaymentConfirmationModal() {
    return new Promise((resolve, reject) => {
      props.showModal({
        hideCloseButton: true,
        title: languageTexts.pre_payment_modal_title,
        text: languageTexts.pre_payment_modal_description,
        confirm: () => {
          resolve(true);
        },
        onRequestClose: () => {
          resolve(false);
        },
      });
    });
  }

  async function showPaymentModal() {
    if (
      !props.paymentProductType ||
      !Object.values(PAYMENT_PRODUCT_TYPE).includes(props.paymentProductType)
    ) {
      props.showModal({ text: `[DEV] props.paymentProductType is required.` });
      return false;
    }

      // 현금 결제 브랜드의 경우 세금계산서 정보 입력
      let cashCampaignPaymentData = null
      if (isKoreaService() && state.brand.billingType === BRAND_DTO_ENUMS.BILLING_TYPE.FREE) {
        const brandTaxInvoiceInfoFormResult = await new Promise<OverlayBrandTaxInvoiceInfoFormResult>(
          (resolve, reject) => {
            props.showOverlays({
              content: () => (
                <OverlayBrandTaxInvoiceInfoForm
                  onComplete={(result) => resolve(result)}
                />
              )
            });
          }
        );
        if (!brandTaxInvoiceInfoFormResult.isSubmitted) {
          return;
        }
        cashCampaignPaymentData = brandTaxInvoiceInfoFormResult.data.campaignPayment;
      }


    return new Promise((resolve, reject) => {
      props.showOverlays({
        content: () => {
          return (
            <Elements stripe={stripePromise}>
              <PaymentCreatePackage
                packageProductType={props.paymentProductType}
                packageAmount={1}
                paymentProducts={[
                  {
                    productType: props.paymentProductType,
                    amount: 1,
                    price: props.paymentPrice,
                    campaignId: props.campaignId,
                    productId: props.productId,
                    postingId: props.postingId,
                  },
                ]}
                cashCampaignPaymentData={cashCampaignPaymentData as Pick<
                  OverlayBrandTaxInvoiceInfoFormResult['data']['campaignPayment'], 
                  keyof OverlayBrandTaxInvoiceInfoFormResult['data']['campaignPayment']
                > | null}
                onSuccess={async (response) => {
                  const payment = response.payment;

                  setTimeout(() => {
                    props.showOverlays({
                      content: () => {
                        return (
                          <PaymentComplete
                            // title={languageTexts.payment_history2}
                            itemType={response.itemType}
                            // amount={response.amount}
                            // price={response.price}
                            // priceTotal={response.priceTotal}
                            // paymentDetails={response.paymentDetails}
                            // commissionAmount={response.commissionAmount}
                            // inspectionFee={response.inspectionFee}
                            onConfirm={() => {
                              props.closeOverlays();
                            }}
                          />
                        );
                      },
                    });
                  }, 400);

                  resolve(true);
                }}
              />
            </Elements>
          );
        },
        onRequestClose: () => {
          resolve(false);
        },
      });
    });
  }

  async function fetchDownloadVideoUrl() {
    const response = await API.PostVideos.downloadUrl({
      id: props.postingVideoId,
      brandToken: props.brandToken,
    });
    const url = response.data.downloadUrl;
    const downloadCount = response.data.downloadCount;
    return [url, downloadCount];
  }

  async function downloadFileFromUrl(url) {
    const extension = (url.split('.').pop() || '').split('?').shift() || 'mp4';
    const downloadFilename =
      props.fileName != null
        ? `${props.fileName}.${extension}`
        : `${Date.now()}.${extension}`;

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFilename;
      link.click();

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error with fetch download:', error);

      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      link.target = '_blank';
      link.click();
    }
  }

  async function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    try {
      // 최신 posting_videos 정보 확인 (campaign_payment_id)
      let postingVideo = await fetchPostingVideo();
      props.onChangePostingVideo(postingVideo);

      // campaign_payment_id가 없을 경우 결제 -> 재요청
      if (props.isPaymentRequired && postingVideo?.campaignPaymentId == null) {
        const isPaid = await processPayment();
        if (!isPaid) {
          return;
        }
        handleClick(e);
        return;
      }

      // download
      const [url, downloadCount] = await fetchDownloadVideoUrl();
      // window.open(url, '_blank')
      await downloadFileFromUrl(url);

      // count 갱신
      props.onChangePostingVideo({
        ...postingVideo,
        downloadCount: downloadCount,
      });
    } catch (e) {
      console.error('[ButtonPostingVideoDownload.handleClick.error]', e);
      props.showModal({
        hideCloseButton: true,
        text: e.message,
      });
    }
  }

  return props.renderButton(handleClick);
};

export default ButtonPostingVideoDownload;
